import { useState, useEffect, useMemo, useCallback, useRef } from 'preact/hooks'
import { X, Search, Copy, Check, AlertCircle } from 'lucide-preact'
import { useVariableCatalog } from '@/hooks/useVariableCatalog'
import { useToast } from '@/hooks/useToast'
import type { VariableCatalogEntry, VariableCatalogGroup, VariableSurface } from '@/types/lookups'

interface VariableCatalogPanelProps {
    open: boolean
    onClose: () => void
    workspaceId: string
    webId: string
    /**
     * Surfaces this builder can actually write to, in display order. Drives both the
     * variable filter and whether the surface switcher renders. The landing page passes a
     * single surface (no switcher); email builders pass body + subject, because a token
     * that resolves in the body is not automatically legal in the subject line.
     */
    surfaces: readonly VariableSurface[]
    /** Extra variables the API catalog doesn't own, appended as their own group. Email
     *  templates use this for the {{BODY}} / {{CTA_TEXT}} / {{CTA_URL}} slot markers, which
     *  are template mechanics rather than web data. */
    extraGroup?: VariableCatalogGroup | null
}

const SURFACE_LABELS: Record<VariableSurface, string> = {
    LandingPage: 'Landing page',
    EmailBody: 'Body',
    EmailSubject: 'Subject line',
}

/**
 * The API's Recipient group. Its tokens are always Available but never carry a SampleValue —
 * the value is per-recipient, so a web-level preview would be fiction shown as live data — and
 * they're the bulk of the available-with-no-sample rows, so they earn copy that says what
 * actually happens instead of the generic line.
 *
 * This is a match on a server-supplied display string. If the group is ever renamed these rows
 * quietly fall back to "No preview available", which is still true, just less specific — a
 * degradation rather than a wrong statement.
 */
const RECIPIENT_GROUP_LABEL = 'Recipient'

/**
 * The token reference panel (ORB-466) shared by the Landing Page and Email builders.
 *
 * Landing pages and emails resolve one shared variable vocabulary, but not every variable
 * works on every surface: a landing page has no recipient, so {{first_name}} is email-only.
 * Before this panel there was no in-product list at all, and a token typed on the wrong
 * surface failed silently and shipped raw to the audience.
 *
 * Each row shows the token, what it means, and the value it resolves to for THIS web right
 * now. The live value is the point: it is the only way to see what the audience will
 * actually read before sending.
 *
 * Clicking a row copies the token. See the insertion note in the footer for why copy rather
 * than cursor insertion: Unlayer runs the email body in a cross-origin iframe and exposes no
 * cursor API, so in-body insertion is handled by Unlayer's own merge-tag menu (which this
 * same catalog seeds), and the landing page body is Webster-generated HTML with no editor to
 * insert into at all.
 */
export function VariableCatalogPanel({
    open,
    onClose,
    workspaceId,
    webId,
    surfaces,
    extraGroup,
}: VariableCatalogPanelProps) {
    const { data, isLoading, error, refetch } = useVariableCatalog(
        open ? workspaceId : undefined,
        open ? webId : undefined,
    )

    const { showError } = useToast()
    const [query, setQuery] = useState('')
    const [activeSurface, setActiveSurface] = useState<VariableSurface>(surfaces[0])
    const [copiedToken, setCopiedToken] = useState<string | null>(null)
    const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const searchRef = useRef<HTMLInputElement>(null)

    // Keep the selected surface valid if the host builder swaps its surface list (e.g. the
    // overlay is reused for a different asset type without unmounting).
    useEffect(() => {
        if (!surfaces.includes(activeSurface)) setActiveSurface(surfaces[0])
    }, [surfaces, activeSurface])

    // Escape must close ONLY this panel. WebsterBuilderOverlay registers a bubble-phase
    // document listener that closes the entire builder, so without stopping propagation here
    // one Escape would tear down both. A capture-phase listener on the same node runs first,
    // and stopPropagation prevents the overlay's handler from ever seeing the event.
    useEffect(() => {
        if (!open) return
        const handler = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return
            e.stopPropagation()
            onClose()
        }
        document.addEventListener('keydown', handler, true)
        return () => document.removeEventListener('keydown', handler, true)
    }, [open, onClose])

    // Reset transient state per open so a reopened panel doesn't show a stale filter or a
    // lingering "Copied" tick, and focus lands on search for keyboard users.
    useEffect(() => {
        if (!open) return
        setQuery('')
        setCopiedToken(null)
        const t = setTimeout(() => searchRef.current?.focus(), 0)
        return () => clearTimeout(t)
    }, [open])

    useEffect(() => () => {
        if (copyTimerRef.current) clearTimeout(copyTimerRef.current)
    }, [])

    const handleCopy = useCallback(async (token: string) => {
        try {
            await navigator.clipboard.writeText(token)
        } catch (e) {
            // Clipboard writes can be refused by permissions policy or a non-secure context.
            // Success stays inline (a toast per row would be unusable across 25 of them), but a
            // failure has to be loud, otherwise the click looks like it silently worked.
            showError('Copy failed', e instanceof Error ? e.message : 'Could not access the clipboard.')
            return
        }
        setCopiedToken(token)
        if (copyTimerRef.current) clearTimeout(copyTimerRef.current)
        copyTimerRef.current = setTimeout(() => setCopiedToken(null), 1500)
    }, [showError])

    // Filter by surface first (correctness), then by the text query (convenience). Groups
    // that empty out are dropped so the user never scrolls past a bare heading.
    const groups = useMemo(() => {
        const all = [
            ...(data?.Groups ?? []),
            ...(extraGroup ? [extraGroup] : []),
        ]
        const needle = query.trim().toLowerCase()
        return all
            .map(group => ({
                Label: group.Label,
                Variables: group.Variables.filter(variable => {
                    if (!variable.Surfaces.includes(activeSurface)) return false
                    if (!needle) return true
                    // Label is searchable too, so someone who thinks in prose ("event title")
                    // finds the token without knowing the snake_case vocabulary.
                    return (
                        variable.Token.toLowerCase().includes(needle)
                        || variable.Name.toLowerCase().includes(needle)
                        || variable.Label.toLowerCase().includes(needle)
                        || variable.Description.toLowerCase().includes(needle)
                    )
                }),
            }))
            .filter(group => group.Variables.length > 0)
    }, [data, extraGroup, query, activeSurface])

    const totalShown = groups.reduce((sum, group) => sum + group.Variables.length, 0)
    // Deliberately ignores extraGroup: those are the host's own template-slot markers, not
    // catalog data. Counting them here let a failed catalog fetch render as a panel quietly
    // listing three slot tokens and nothing else — a short list that looks complete is the
    // exact failure this panel exists to eliminate, so the fetch's own state drives both the
    // skeleton and the error, and the slot tokens render underneath either one.
    const hasCatalogData = (data?.Groups?.length ?? 0) > 0

    if (!open) return null

    return (
        <div className="fixed inset-0 z-[60] flex" aria-modal="true" role="dialog" aria-label="Variables">
            <div className="flex-1 bg-slate-900/40" onClick={onClose} />

            <aside className="ml-auto flex h-full w-full flex-col border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl sm:w-[460px] lg:w-[540px]">
                {/* Header */}
                <div className="flex-shrink-0 flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-slate-200 dark:border-slate-700">
                    <div className="min-w-0">
                        <h2 className="text-base font-semibold text-slate-900 dark:text-white">Variables</h2>
                        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                            Live values for this web, exactly what your audience will see.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close variables"
                        className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Surface switcher — only when the builder writes to more than one surface.
                    Filtering by where the user is beats badging every row with where it works. */}
                {surfaces.length > 1 && (
                    <div className="flex-shrink-0 px-5 pt-3">
                        <div
                            role="group"
                            aria-label="Filter variables by where you are writing"
                            className="inline-flex items-center gap-0.5 rounded-lg bg-slate-100 dark:bg-slate-800 p-0.5"
                        >
                            {surfaces.map(surface => {
                                const selected = surface === activeSurface
                                return (
                                    <button
                                        key={surface}
                                        type="button"
                                        aria-pressed={selected}
                                        onClick={() => setActiveSurface(surface)}
                                        className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                                            selected
                                                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                                                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                                        }`}
                                    >
                                        {SURFACE_LABELS[surface]}
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                )}

                {/* Search */}
                <div className="flex-shrink-0 px-5 py-3">
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                        <input
                            ref={searchRef}
                            type="search"
                            value={query}
                            onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
                            placeholder="Search variables"
                            aria-label="Search variables"
                            className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 pl-8 pr-3 py-1.5 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                        />
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto">
                    {isLoading && !hasCatalogData && (
                        <div className="px-5 py-6 space-y-5">
                            {[0, 1, 2, 3, 4].map(i => (
                                <div key={i} className="space-y-1.5">
                                    <div className="h-3 w-40 rounded bg-slate-200 dark:bg-slate-700 animate-pulse motion-reduce:animate-none" />
                                    <div className="h-3 w-28 rounded bg-slate-100 dark:bg-slate-800 animate-pulse motion-reduce:animate-none" />
                                </div>
                            ))}
                        </div>
                    )}

                    {error && !hasCatalogData && (
                        <div className="px-5 py-6">
                            <div className="flex items-start gap-2.5">
                                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-400" />
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                                        Couldn't load variables
                                    </p>
                                    <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400 break-words">{error}</p>
                                    <button
                                        type="button"
                                        onClick={refetch}
                                        className="mt-2 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
                                    >
                                        Try again
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {!isLoading && !error && totalShown === 0 && (
                        <div className="px-5 py-10 text-center">
                            <p className="text-sm text-slate-600 dark:text-slate-300">
                                {query.trim()
                                    ? `Nothing matches "${query.trim()}"`
                                    : `No variables resolve on the ${SURFACE_LABELS[activeSurface].toLowerCase()}`}
                            </p>
                            {query.trim() && (
                                <button
                                    type="button"
                                    onClick={() => setQuery('')}
                                    className="mt-2 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
                                >
                                    Clear search
                                </button>
                            )}
                        </div>
                    )}

                    {groups.map(group => (
                        <section key={group.Label}>
                            <h3 className="sticky top-0 z-10 flex items-baseline gap-2 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm px-5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                                {group.Label}
                                <span className="font-normal tracking-normal text-slate-300 dark:text-slate-600">
                                    {group.Variables.length}
                                </span>
                            </h3>
                            <ul>
                                {group.Variables.map(variable => (
                                    <VariableRow
                                        key={variable.Token}
                                        variable={variable}
                                        perRecipient={group.Label === RECIPIENT_GROUP_LABEL}
                                        copied={copiedToken === variable.Token}
                                        onCopy={handleCopy}
                                    />
                                ))}
                            </ul>
                        </section>
                    ))}
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 border-t border-slate-200 dark:border-slate-700 px-5 py-2.5">
                    <p className="text-[11px] leading-relaxed text-slate-400 dark:text-slate-500">
                        Click a variable to copy it. In the email body you can also pick it from the
                        editor's merge tag menu, or type <span className="font-mono text-slate-500 dark:text-slate-400">{'{'}</span> to search inline.
                    </p>
                </div>
            </aside>
        </div>
    )
}

interface VariableRowProps {
    variable: VariableCatalogEntry
    /** Row belongs to the Recipient group, so its missing sample has a specific explanation
     *  rather than the generic one. See RECIPIENT_GROUP_LABEL. */
    perRecipient: boolean
    copied: boolean
    onCopy: (token: string) => void
}

/**
 * One variable. Reading order is token, then resolved value, then description: you scan for
 * the token you want, confirm against what it renders as, and only read the description when
 * two tokens look alike ({{event_join_url}} vs {{add_to_calendar_url}}).
 *
 * Description rather than Label carries the third line on purpose. Label is the short form and
 * wins in Unlayer's compact menu; here there's room for the prose that actually disambiguates,
 * and showing both would just restate ("Event Title" over "the event title"). Label stays
 * searchable so either vocabulary finds the row.
 *
 * Unavailable variables stay listed rather than hidden, so "where did that token go" never
 * happens. They're separated by muted type AND an explicit reason line, never by dimming
 * alone, because color can't be the sole carrier of meaning.
 *
 * The second line has three states, not two, because Available and SampleValue are independent:
 * Available says the token WORKS here, SampleValue says we have something to show for it. A
 * token can work and still have no preview — every Recipient token, and add_to_calendar_url
 * without a configured API host — so each state gets its own sentence.
 */
function VariableRow({ variable, perRecipient, copied, onCopy }: VariableRowProps) {
    const { Token, Description, SampleValue, Available, UnavailableReason } = variable

    return (
        <li>
            <button
                type="button"
                onClick={() => onCopy(Token)}
                title={`Copy ${Token}`}
                className="group w-full text-left px-5 py-2.5 border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 transition-colors motion-reduce:transition-none"
            >
                <div className="flex items-center justify-between gap-3">
                    <code
                        className={`font-mono text-[13px] leading-5 truncate ${
                            Available
                                ? 'text-slate-900 dark:text-slate-100'
                                : 'text-slate-400 dark:text-slate-500'
                        }`}
                    >
                        {Token}
                    </code>
                    <span
                        className={`flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-medium transition-opacity motion-reduce:transition-none ${
                            copied
                                ? 'text-primary-600 dark:text-primary-400 opacity-100'
                                : 'text-slate-400 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100'
                        }`}
                    >
                        {copied
                            ? <><Check className="w-3 h-3" aria-hidden="true" />Copied</>
                            : <><Copy className="w-3 h-3" aria-hidden="true" />Copy</>}
                    </span>
                </div>

                {!Available ? (
                    /* The server's reason ("No agenda items added yet") tells the user how to
                       fix it; the generic line only tells them something is missing. */
                    <p className="mt-0.5 text-[13px] leading-5 text-slate-400 dark:text-slate-500 italic break-words line-clamp-2">
                        {UnavailableReason ?? 'Not set for this web'}
                    </p>
                ) : SampleValue !== null ? (
                    <p className="mt-0.5 text-[13px] leading-5 text-slate-700 dark:text-slate-300 break-words line-clamp-2">
                        {SampleValue}
                    </p>
                ) : (
                    /* Works, but there's nothing to preview. Sits at a lighter weight than a real
                       value and a heavier one than an unavailable row, so the three states read
                       apart at a glance — but the sentence carries the meaning, since the token
                       above is already the only thing colour distinguishes. */
                    <p className="mt-0.5 text-[13px] leading-5 text-slate-500 dark:text-slate-400 italic break-words line-clamp-2">
                        {perRecipient
                            ? 'Filled in for each recipient at send time'
                            : 'No preview available'}
                    </p>
                )}

                <p className="mt-0.5 text-[11px] leading-4 text-slate-400 dark:text-slate-500">
                    {Description}
                </p>
            </button>
        </li>
    )
}
